import { Session } from 'next-auth';
import { useSession } from 'next-auth/react';
import { createContext, useContext, useMemo } from 'react';
import { FullProfileData } from '../UserProvider/ProfileData';
import { useHannibalQuery } from '../schema/hannibal/HannibalContext';
import { SuburbAutocompleteDocument } from '../schema/hannibal/types.generated';
import { AsyncResult } from '../types/AsyncResult';
import { ChildrenOnly } from '../types/ChildrenOnly';
import { returnVoid } from '../util/returnVoid';

export type CorporateFullProfileData = FullProfileData & { clientUserId?: string; isPrimary: true };

export type CorporateUserResult = AsyncResult<CorporateFullProfileData>;

export const CorporateUserContext = createContext<CorporateUserResult>({
  loading: true,
  refetch: returnVoid,
});

export const useCorporateUserData = () => useContext(CorporateUserContext);

type CorporateSession = (Session & { profile?: CorporateFullProfileData }) | null;

export const CorporateUserProvider = ({ children }: ChildrenOnly) => {
  const { data: nextAuthSession, status } = useSession();
  const session: CorporateSession = nextAuthSession;

  let query = '';

  const homeAddress = session?.profile?.homeAddress;
  const unverifiedHomeAddress = session?.profile?.unverifiedHomeAddress;

  // Attempt to verify an external suburb/state/postcode if present and the user hasn't already supplied a suburb
  const attemptHomeAddressVerification =
    !homeAddress?.suburb && (unverifiedHomeAddress?.suburb || unverifiedHomeAddress?.postcode);

  if (attemptHomeAddressVerification) {
    query = `${unverifiedHomeAddress?.suburb}, ${unverifiedHomeAddress?.state} ${unverifiedHomeAddress?.postcode}`;
  }

  const { data: suburbQueryData, loading: suburbQueryLoading } = useHannibalQuery(
    SuburbAutocompleteDocument,
    {
      variables: { query },
      skip: query.trim() === '',
    },
  );

  const accountData: CorporateUserResult = useMemo(() => {
    const data = session?.profile;

    if (
      data &&
      attemptHomeAddressVerification &&
      !suburbQueryLoading &&
      suburbQueryData?.searchSuburbsByName.suburbs
    ) {
      if (!data.homeAddress) {
        data.homeAddress = {};
      }

      const firstSuburbMatch = suburbQueryData.searchSuburbsByName.suburbs[0];

      data.homeAddress.suburb = firstSuburbMatch?.name;
      data.homeAddress.state = firstSuburbMatch?.state;
      data.homeAddress.postcode = firstSuburbMatch?.postcode;
    }

    return {
      data,
      loading: status === 'loading',
    };
  }, [
    attemptHomeAddressVerification,
    status,
    session?.profile,
    suburbQueryData?.searchSuburbsByName.suburbs,
    suburbQueryLoading,
  ]);

  return (
    <CorporateUserContext.Provider value={accountData}>{children}</CorporateUserContext.Provider>
  );
};
